import { Col, Row, Typography } from "antd";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { DatabaseReference } from "firebase/database";

import { RootState } from "../../../app/store";
import {
  setSelectedCountryId,
  userRequestListAsync,
} from "../../../features/userRequest/userRequestListSlice";
import UserRequestDTO from "../../../shared/dtos/userRequestDTO";
import UserRequestCard from "./userRequestCard";
import { DispatcherMap } from "../../map/Map";
import getDestinationsByUserRequestHelper from "../../../utils/helpers/getDestinationsByUserRequestHelper";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import { updateUserRequestValue } from "../../../features/userRequest/pendingUserRequestSlice";
import { useLocation, useNavigate } from "react-router-dom";
import MapDTO from "../../../shared/dtos/mapDTO";
import ProviderDTO from "../../../shared/dtos/providerDTO";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import Filter from "../create/utils/filterComponent";

import { selectSelectedCountryId } from "../../../utils/helpers/selectorHelper";
import { setCountry } from "../../../features/map/mapSlice";
import { COUNTRIES } from "../../../utils/constants/countries";
import ContainerLayout from "../../common/containerLayout";
import UserRequestProps from "../../../propInterfaces/UserRequestProps";
import { serviceTypeListAsync } from "../../../features/serviceType/serviceTypeListSlice";
import { adminsForSelectAsync } from "../../../features/admin/adminsForSelectSlice";

import "../../../shared/styles/dark.css";
import "./styles.css";
import {
  subscribeDriverTracking,
  unSubscribe,
} from "../../../utils/externals/firebase/firebaseDB";
import { getProviderById } from "../../../services/adminProviderApi";

const { Title } = Typography;
const REFRESH_INTERVAL = 2 * 60 * 1000;

interface UserRequestDataMapProps {
  directions?: MapDTO | null;
  provider?: ProviderDTO | null;
  bookingId?: string | null;
}

let driversTrackingRef: DatabaseReference | null = null;

const UserRequestList: React.FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const requestIdRef = useRef(0);
  const [refreshData, setRefreshData] = useState(true);
  const [userRequestDataMap, setUserRequestDataMap] =
    useState<UserRequestDataMapProps | null>(null);
  const [activeProviders, setActiveProviders] = useState<Array<ProviderDTO>>(
    [],
  );
  const [selectedTrips, setSelectedTrips] = useState<UserRequestDTO[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<ProviderDTO | null>(
    null,
  );

  const { selectedServiceTypeId } = useAppSelector(
    (state: RootState) => state.serviceTypeList,
  );
  const { value: userRequests, status } = useAppSelector(
    (state: RootState) => state.userRequestList,
  );
  const selectedAdminId = useAppSelector(
    (state: RootState) => state.userRequestList.selectedAdminId,
  );

  useEffect(() => {
    if (state?.refreshData) {
      setRefreshData(true);
      window.history.replaceState({}, "");
    }
  }, [state]);

  useEffect(() => {
    dispatch(adminsForSelectAsync());
    dispatch(serviceTypeListAsync());
  }, [dispatch]);

  const fetchProviderData = async () => {
    if (!selectedProvider || selectedProvider.id) return;

    const provider = await getProviderById(selectedProvider?.providerId!);

    if (provider) setSelectedProvider((prev) => ({ ...prev, ...provider }));
  };

  useEffect(() => {
    fetchProviderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvider]);

  const resetInterval = useCallback(
    (body: UserRequestProps) => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      intervalRef.current = setInterval(() => {
        dispatch(userRequestListAsync(body));
      }, REFRESH_INTERVAL);
    },
    [dispatch],
  );

  const fetchData = useCallback(async () => {
    setRefreshData(false);
    setSelectedTrips([]);

    const currentRequestId = ++requestIdRef.current;

    let country = selectSelectedCountryId()
      ? COUNTRIES.find((x) => x.value === selectSelectedCountryId())
      : null;

    let body: UserRequestProps = {
      adminId: selectedAdminId,
    };

    if (country) {
      dispatch(setCountry(country));
      dispatch(setSelectedCountryId(country.value));
      body.countryId = country.value;
    }

    await dispatch(userRequestListAsync(body));

    // solo el último request puede crear el intervalo
    if (currentRequestId === requestIdRef.current) {
      resetInterval(body);
    }
  }, [dispatch, selectedAdminId, resetInterval]);

  // carga inicial y cuando cambia admin
  useEffect(() => {
    fetchData();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [fetchData]);

  useEffect(() => {
    if (refreshData) {
      fetchData();
    }
  }, [refreshData, fetchData]);

  const handleDoubleClick = (
    event: React.MouseEvent,
    userRequest: UserRequestDTO,
  ) => {
    if (!event.altKey || userRequest.providerId) return;

    dispatch(updateUserRequestValue([userRequest]));
    navigate(WebRoutes.AssignProviderRoute, {
      replace: true,
    });
  };

  useEffect(() => {
    setActiveProviders([]);
    if (driversTrackingRef) unSubscribe(driversTrackingRef);
    if (!selectedTrips.length)
      driversTrackingRef = subscribeDriverTracking(setActiveProviders);
  }, [selectedTrips, selectedServiceTypeId]);

  const map = useMemo(
    () => (
      <DispatcherMap
        destinations={userRequestDataMap?.directions}
        activeProvider={userRequestDataMap?.provider || null}
        providers={activeProviders}
        showProviders={!!activeProviders.length}
        bookingId={userRequestDataMap?.bookingId}
        setProvider={setSelectedProvider}
        selectedProvider={selectedProvider}
      />
    ),
    [
      userRequestDataMap?.directions,
      userRequestDataMap?.provider,
      userRequestDataMap?.bookingId,
      activeProviders,
      selectedProvider,
    ],
  );

  const handleSelectedTrips = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    userRequest: UserRequestDTO,
    add: boolean,
  ) => {
    setUserRequestDataMap({
      directions: getDestinationsByUserRequestHelper(userRequest),
      provider: userRequest.provider,
      bookingId: userRequest.bookingId,
    });
    if (event.altKey)
      setSelectedTrips((userRequests) => {
        if (add) {
          return [...userRequests, userRequest];
        } else {
          return userRequests.filter((r) => r.id !== userRequest.id);
        }
      });
    else setSelectedTrips([userRequest]);
  };

  return (
    <ContainerLayout
      title="Listado de viajes"
      showNotification={false}
      showSound
    >
      <Col span={24}>
        <Filter setRefreshData={setRefreshData} selectedTrips={selectedTrips} />
      </Col>
      {status === "loading" && !userRequests?.length && <Col span={8}></Col>}
      {!!userRequests?.length && (
        <Col span={8} className="card-container" id="provider">
          <Row gutter={[24, 12]}>
            {userRequests?.map((userRequest: UserRequestDTO) => {
              return (
                <Col
                  key={userRequest.id}
                  onDoubleClick={(e) => handleDoubleClick(e, userRequest)}
                  span={24}
                >
                  <UserRequestCard
                    key={userRequest.id}
                    userRequest={userRequest}
                    isChecked={selectedTrips?.some(
                      (i) => i.id === userRequest.id,
                    )}
                    onSelect={handleSelectedTrips}
                    setRefreshData={setRefreshData}
                  />
                </Col>
              );
            })}
          </Row>
        </Col>
      )}
      {status !== "loading" && !userRequests?.length && (
        <Col span={8} className="loading-container error-container">
          <ExclamationCircleOutlined />
          <Title level={3}>No hay datos</Title>
        </Col>
      )}
      <Col span={16}>{map}</Col>
    </ContainerLayout>
  );
};

export default UserRequestList;
